import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from '@cargomatic/ui';
import {
  Bell,
  ChevronDown,
  CreditCard,
  FileText,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  Mail,
  Settings,
  Truck,
} from 'lucide-react';
import CargomaticLogo from 'img/cargomatic-logo-dark.png';
import SessionStore from 'stores/SessionStore';
import TrackingHeader from 'components/ui/tracking-header';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import classNames from 'classnames';
import { getCustomerReports } from 'utils/UnifiedViewWebAPIUtils';
import Navbar from '../v2/navs/Navbar';
import UserActionCreators from '../../actions/UserActionCreators';
import WhiteLabel from '../../utils/whitelabel.js';
import NavbarOverlayBanner from '../v2/navbar-overlay-banner';
import ShipmentSearch from './shipment-search';
import { Avatar } from '../modules/user/Avatar';
import LanguageSelector from './language-selector';
import { NavScrollFade } from './NavScrollFade';

const SUPPORT_MAILTO = 'mailto:support@cargomatic.com';

const navLinkClass =
  'group inline-flex h-10 items-center justify-center rounded-md px-3 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-gray-700 hover:text-white focus:bg-gray-700 focus:text-white focus:outline-none disabled:pointer-events-none disabled:opacity-50';

const navLinkActiveClass = 'bg-gray-900 text-white';

const navTriggerClass = `${navLinkClass} gap-1`;

const dropdownItemClass =
  'gap-2 text-gray-900 no-underline hover:no-underline focus:bg-gray-100 focus:text-gray-900 focus:outline-none focus-visible:outline-none';

function logout() {
  UserActionCreators.logout();
}

const isShipper = () => {
  return SessionStore.isShipper();
};

const isMasterAccountUser = () => {
  return SessionStore.isMasterAccountUser();
};

class ShipperNavigation extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      logo: CargomaticLogo,
      reports: [],
    };
  }

  componentDidMount() {
    const logoFunction = async () => {
      const logo = await WhiteLabel.getClientLogo(false);
      if (logo) {
        this.setState({ logo });
      }
    };
    getCustomerReports()
      .then((reports) => {
        this.setState({ reports });
      })
      .catch((e) => console.error(e));
    logoFunction();
  }

  renderMobileNav = (onClose) => {
    const items = [];
    const { t } = this.props;

    if (!isMasterAccountUser()) {
      items.push(
        <NavLink
          className="nav-item"
          to="/quotes"
          onClick={onClose}
          key="mobile-quotes"
        >
          <span>{t('GET_QUOTE_SHIPMENT')}</span>
        </NavLink>,
      );
      items.push(
        <NavLink
          className="nav-item"
          to="/booking"
          onClick={onClose}
          key="mobile-booking"
        >
          <span>{t('BOOK_SHIPMENT')}</span>
        </NavLink>,
      );
    }

    items.push(
      <NavLink
        className="nav-item"
        to="/shipments"
        onClick={onClose}
        key="mobile-shipments"
      >
        <span>{t('SHIPMENT_HISTORY')}</span>
      </NavLink>,
    );

    if (!isMasterAccountUser()) {
      items.push(
        <NavLink
          className="nav-item"
          to="/invoices"
          onClick={onClose}
          key="mobile-invoices"
        >
          <span>{t('MY_INVOICES')}</span>
        </NavLink>,
      );
    }

    if (isShipper()) {
      items.push(
        <NavLink
          className="nav-item"
          to="/accessorials"
          onClick={onClose}
          key="mobile-accessorials"
        >
          <span>{t('ACCESSORIAL', { count: 2 })}</span>
        </NavLink>,
      );
    }

    items.push(
      <NavLink
        className="nav-item"
        to="/profile"
        onClick={onClose}
        key="mobile-profile"
      >
        <span>{titleize(t('PROFILE', { count: 1 }))}</span>
      </NavLink>,
    );

    items.push(
      <a
        href={SUPPORT_MAILTO}
        className="nav-item"
        onClick={onClose}
        key="mobile-contact-support"
      >
        <span>{t('CONTACT_SUPPORT')}</span>
      </a>,
    );

    items.push(
      <div
        key="language-selector-mobile"
        className={classNames('flex items-center justify-center nav-item', {
          hidden: LANGUAGE_SELECTION_DISABLED,
        })}
      >
        <LanguageSelector mobile />
      </div>,
    );

    items.push(
      <a href="#" className="nav-item" onClick={logout} key="mobile-sign-out">
        {t('SIGN_OUT')}
      </a>,
    );

    return items;
  };

  renderRightNav = () => {
    const { t } = this.props;
    const shipperName = SessionStore.getUser().first_name;
    const userId = SessionStore.getUser()._id;

    const profileButton = (
      <button
        type="button"
        title={shipperName}
        className="flex-shrink-0 flex h-8 w-8 items-center justify-center text-sm bg-gray-800 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
        aria-haspopup="true"
        aria-label={shipperName}
      >
        <Avatar
          key={userId}
          userId={userId}
          altName={shipperName || 'Profile'}
          className="w-8 h-8 rounded-full object-cover"
        />
      </button>
    );

    return [
      <div
        key="language-selector"
        className={classNames(
          'flex items-center justify-center nav-item mr-2',
          { hidden: LANGUAGE_SELECTION_DISABLED },
        )}
      >
        <LanguageSelector />
      </div>,
      <DropdownMenu key="right-nav">
        <DropdownMenuTrigger asChild>{profileButton}</DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem asChild className={dropdownItemClass}>
            <NavLink to="/profile">
              <Settings className="h-4 w-4" />
              <span>{titleize(t('MANAGE'))}</span>
            </NavLink>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className={dropdownItemClass}>
            <NavLink to="/billing">
              <CreditCard className="h-4 w-4" />
              <span>{t('BILLING')}</span>
            </NavLink>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className={dropdownItemClass}>
            <NavLink to="/notifications">
              <Bell className="h-4 w-4" />
              <span>{titleize(t('NOTIFICATION', { count: 2 }))}</span>
            </NavLink>
          </DropdownMenuItem>
          {!isMasterAccountUser() && (
            <DropdownMenuItem asChild className={dropdownItemClass}>
              <NavLink to="/invoices">
                <Mail className="h-4 w-4" />
                <span>{t('MY_INVOICES')}</span>
              </NavLink>
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild className={dropdownItemClass}>
            <a href={SUPPORT_MAILTO}>
              <LifeBuoy className="h-4 w-4" />
              <span>{t('CONTACT_SUPPORT')}</span>
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem className={dropdownItemClass} onClick={logout}>
            <LogOut className="h-4 w-4" />
            <span>{t('SIGN_OUT')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>,
    ];
  };

  renderShipmentsDropdown() {
    const { t } = this.props;
    const { reports } = this.state;
    const showDashboard = !!reports?.length;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className={navTriggerClass}>
            {t('SHIPMENT', { count: 2 })}
            <ChevronDown className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          {!isMasterAccountUser() && (
            <>
              {showDashboard && (
                <DropdownMenuItem asChild className={dropdownItemClass}>
                  <NavLink to="/unifiedview/dashboard">
                    <LayoutDashboard className="h-4 w-4" />
                    <span>{titleize(t('DASHBOARD'))}</span>
                  </NavLink>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem asChild className={dropdownItemClass}>
                <NavLink to="/quotes">
                  <FileText className="h-4 w-4" />
                  <span>{t('GET_QUOTE_SHIPMENT')}</span>
                </NavLink>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className={dropdownItemClass}>
                <NavLink to="/booking">
                  <Truck className="h-4 w-4" />
                  <span>{t('BOOK_SHIPMENT')}</span>
                </NavLink>
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuItem asChild className={dropdownItemClass}>
            <NavLink to="/shipments">
              <FileText className="h-4 w-4" />
              <span>{t('SHIPMENT_HISTORY')}</span>
            </NavLink>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  render() {
    if (SessionStore.isQuotesOnly()) {
      return (
        <div className="absolute top-0 left-0 w-full" key="tracking-header">
          <TrackingHeader />
        </div>
      );
    }

    const brandLogo = (
      <NavLink to="/" key="brand-logo">
        <img alt="Cargomatic" src={this.state.logo} style={{ height: 25 }} />
      </NavLink>
    );

    const overlay = (
      <NavbarOverlayBanner
        text={SessionStore.getUser().company_name || ''}
        showButton
        key="overlay-banner"
      />
    );
    const isLoggedInAs = !!SessionStore.getLoginAs();
    const { t } = this.props;
    return (
      <>
        <Navbar
          logo={brandLogo}
          renderRightNav={this.renderRightNav}
          renderMobileNav={this.renderMobileNav}
        >
          <NavScrollFade key="primary-nav">
            <NavigationMenu className="max-w-none">
              <NavigationMenuList className="flex-nowrap items-center justify-start gap-1 space-x-0">
                <NavigationMenuItem className="flex-shrink-0">
                  {this.renderShipmentsDropdown()}
                </NavigationMenuItem>
                {!isMasterAccountUser() && (
                  <NavigationMenuItem className="flex-shrink-0">
                    <NavigationMenuLink asChild>
                      <NavLink
                        to="/invoices"
                        className={navLinkClass}
                        activeClassName={navLinkActiveClass}
                      >
                        {t('MY_INVOICES')}
                      </NavLink>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                )}
                {isShipper() && (
                  <NavigationMenuItem className="flex-shrink-0">
                    <NavigationMenuLink asChild>
                      <NavLink
                        to="/accessorials"
                        className={navLinkClass}
                        activeClassName={navLinkActiveClass}
                      >
                        {t('ACCESSORIAL', { count: 2 })}
                      </NavLink>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                )}
              </NavigationMenuList>
            </NavigationMenu>
          </NavScrollFade>
          <div className="w-44 flex-shrink-0 xl:w-60" key="shipment-search">
            <ShipmentSearch
              menuStyle={{
                position: 'fixed',
                overflowX: 'hidden',
                maxHeight: '50vh',
                zIndex: 100,
                cursor: 'pointer',
                overflowY: 'scroll',
              }}
            />
          </div>
        </Navbar>
        <div key="overlay-item">{isLoggedInAs && overlay}</div>
      </>
    );
  }
}

export default withTranslation()(ShipperNavigation);
